import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import type { ServiceAgreement } from "@/contracts";

import {
  cancelServiceAgreementMutation,
  closeServiceAgreementMutation,
  issueServiceAgreementMutation,
  renewServiceAgreementMutation,
  serviceAgreementQuery,
} from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ErrorBlock, LoadingBlock } from "@/components/ui/page";
import { useApiClient } from "@/lib/api-context";
import { formatDate } from "@/lib/format";

import { AttestServiceSignatureDialog } from "./attest-service-signature-dialog";
import { ServiceAgreementCreateDialog } from "./service-agreement-create-dialog";
import { SERVICE_STATUS_LABELS, SERVICE_STATUS_TONES } from "./service-labels";

export function ServiceAgreementPage() {
  const { t } = useTranslation("services");
  const client = useApiClient();
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAct = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const queryClient = useQueryClient();
  const agreement = useQuery(serviceAgreementQuery(client, id));
  const [signing, setSigning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState<"issue" | "close" | "cancel" | "renew" | null>(
    null,
  );
  const [pdfPending, setPdfPending] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const settled = async () => {
    setConfirming(null);
    await queryClient.invalidateQueries({ queryKey: ["service-agreements"] });
  };

  const issue = useMutation({ ...issueServiceAgreementMutation(client), onSuccess: settled });
  const close = useMutation({ ...closeServiceAgreementMutation(client), onSuccess: settled });
  const cancel = useMutation({ ...cancelServiceAgreementMutation(client), onSuccess: settled });
  const renew = useMutation({
    ...renewServiceAgreementMutation(client),
    onSuccess: async (draft) => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ["service-agreements"] });
      await navigate(`/service-agreements/${draft.id}`);
    },
  });

  const data = agreement.data;
  const failure = issue.error ?? close.error ?? cancel.error ?? renew.error;
  const canRenew =
    canAct &&
    data !== undefined &&
    (data.status === "ISSUED" || data.status === "SIGNED" || data.status === "CLOSED");

  const downloadPdf = async () => {
    setPdfError(null);
    setPdfPending(true);
    try {
      const { blob, filename } = await client.getBlob(`/service-agreements/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? `servicio-${data?.number ?? id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : t("pdfDownloadFailed"));
    } finally {
      setPdfPending(false);
    }
  };

  return (
    <>
      <div className="print:hidden">
        <div className="mb-5">
          <Link
            to="/contracts/services"
            className="text-sm text-ink-muted underline underline-offset-2"
          >
            {t("backToList")}
          </Link>
          <h1 className="mt-2 text-lg font-semibold tracking-tight text-ink">
            {data ? data.number : t("detailFallbackTitle")}
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">{t("detailDescription")}</p>
        </div>

        {data ? (
          <Card className="mb-4">
            <CardBody>
              {failure ? <Alert className="mb-3">{failure.message}</Alert> : null}
              {pdfError ? <Alert className="mb-3">{pdfError}</Alert> : null}
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={SERVICE_STATUS_TONES[data.status]}>
                  {SERVICE_STATUS_LABELS[data.status]}
                </Badge>
                {data.status === "DRAFT" && canAct ? (
                  <>
                    <Button onClick={() => setConfirming("issue")}>{t("issue")}</Button>
                    <Button variant="secondary" onClick={() => setEditing(true)}>
                      {t("editDraft")}
                    </Button>
                  </>
                ) : null}
                {data.status !== "DRAFT" ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => void downloadPdf()}
                      loading={pdfPending}
                    >
                      {t("downloadPdf")}
                    </Button>
                    <Button variant="secondary" onClick={() => window.print()}>
                      {t("printFileCopy")}
                    </Button>
                  </>
                ) : null}
                {data.status === "ISSUED" && canAct ? (
                  <Button variant="secondary" onClick={() => setSigning(true)}>
                    {t("recordSignature")}
                  </Button>
                ) : null}
                {(data.status === "ISSUED" || data.status === "SIGNED") && canAct ? (
                  <Button variant="secondary" onClick={() => setConfirming("close")}>
                    {t("close")}
                  </Button>
                ) : null}
                {(data.status === "DRAFT" || data.status === "ISSUED") && canAct ? (
                  <Button variant="secondary" onClick={() => setConfirming("cancel")}>
                    {t("cancel")}
                  </Button>
                ) : null}
                {canRenew ? (
                  <Button variant="secondary" onClick={() => setConfirming("renew")}>
                    {t("renew")}
                  </Button>
                ) : null}
              </div>
              {data.status !== "DRAFT" ? (
                <Alert tone="info" title={t("printHintTitle")} announce={false} className="mt-3">
                  {t("printHintBody")}
                </Alert>
              ) : (
                <Alert tone="info" title={t("notPrintableTitle")} announce={false} className="mt-3">
                  {t("notPrintableBody")}
                </Alert>
              )}
              {data.renewedFromId ? (
                <Alert tone="info" title={t("renewalDraftTitle")} announce={false} className="mt-3">
                  {t("renewalDraftBody")}
                </Alert>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
      </div>

      {agreement.isPending ? <LoadingBlock label={t("loadingDetail")} /> : null}
      {agreement.isError ? (
        <ErrorBlock error={agreement.error} onRetry={() => void agreement.refetch()} />
      ) : null}
      {data ? <ServiceDocument agreement={data} /> : null}

      {data && signing ? (
        <AttestServiceSignatureDialog agreement={data} onClose={() => setSigning(false)} />
      ) : null}

      {data && editing ? (
        <ServiceAgreementCreateDialog
          open
          agreement={data}
          onClose={() => setEditing(false)}
        />
      ) : null}

      <ConfirmDialog
        open={confirming === "issue"}
        title={t("issueConfirmTitle")}
        confirmLabel={t("issue")}
        pending={issue.isPending}
        onConfirm={() => issue.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("issueConfirmBody")}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "close"}
        title={t("closeConfirmTitle")}
        confirmLabel={t("close")}
        pending={close.isPending}
        onConfirm={() => close.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("closeConfirmBody")}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "cancel"}
        title={t("cancelConfirmTitle")}
        confirmLabel={t("cancelConfirmLabel")}
        destructive
        pending={cancel.isPending}
        onConfirm={() => cancel.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("cancelConfirmBody")}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === "renew"}
        title={t("renewConfirmTitle")}
        confirmLabel={t("renew")}
        pending={renew.isPending}
        onConfirm={() => renew.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("renewConfirmBody")}
      </ConfirmDialog>
    </>
  );
}

function ServiceDocument({ agreement }: { agreement: ServiceAgreement }) {
  const { t } = useTranslation("services");

  return (
    <article className="loan-agreement-document space-y-6 text-sm text-ink">
      {agreement.draftNotice ? (
        <p className="break-inside-avoid rounded-lg border border-danger bg-danger-soft p-3 text-xs leading-relaxed font-medium whitespace-pre-line text-danger">
          {agreement.draftNotice}
        </p>
      ) : null}

      <header className="border-b border-border-subtle pb-4">
        <h2 className="text-base font-semibold">
          Contrato de servicio de monitoreo — {agreement.number}
        </h2>
        <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Entry term={t("metaOrganization")} value={agreement.organizationName} />
          <Entry term={t("metaSite")} value={`${agreement.siteName}, ${agreement.siteAddress}`} />
          <Entry term={t("metaCustomer")} value={agreement.customerName} />
          <Entry
            term={t("metaTariff")}
            value={t("metaTariffValue", {
              currency: agreement.currency,
              alta: agreement.altaUsd.toFixed(2),
              abono: agreement.monthlyAbonoUsd.toFixed(2),
              sensors: agreement.includedSensors,
            })}
          />
          {agreement.loanAgreementId && agreement.loanAgreementNumber ? (
            <Entry
              term={t("metaLoan")}
              value={
                <Link
                  to={`/loan-agreements/${agreement.loanAgreementId}`}
                  className="text-brand underline underline-offset-2 print:text-ink print:no-underline"
                >
                  {agreement.loanAgreementNumber}
                </Link>
              }
            />
          ) : null}
          <Entry
            term={t("metaTerm")}
            value={t("metaTermValue", {
              months: agreement.termMonths,
              starts: formatDate(agreement.startsOn),
              ends: formatDate(agreement.endsOn),
            })}
          />
          <Entry
            term={t("metaStatus")}
            value={SERVICE_STATUS_LABELS[agreement.status]}
            className="print:hidden"
          />
        </dl>
      </header>

      {agreement.clauses.length === 0 ? (
        <p className="text-ink-muted">{t("noClausesYet")}</p>
      ) : (
        <section className="space-y-4">
          {agreement.clauses.map((clause) => (
            <div key={clause.title} className="break-inside-avoid">
              <h3 className="mb-1 text-sm font-semibold">{clause.title}</h3>
              {clause.body.split("\n\n").map((paragraph, index) => (
                <p key={index} className="mb-2 leading-relaxed whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
              {clause.titleEn || clause.bodyEn ? (
                <div className="mt-3 rounded-md border border-border-subtle bg-surface-muted p-3 text-ink-muted">
                  {clause.titleEn ? (
                    <h4 className="mb-1 text-sm font-medium">{clause.titleEn}</h4>
                  ) : null}
                  {clause.bodyEn
                    ? clause.bodyEn.split("\n\n").map((paragraph, index) => (
                        <p key={index} className="mb-2 text-xs leading-relaxed whitespace-pre-line">
                          {paragraph}
                        </p>
                      ))
                    : null}
                </div>
              ) : null}
            </div>
          ))}
        </section>
      )}

      {agreement.fileCopyNotice ? (
        <p className="break-inside-avoid rounded-lg border border-border-subtle bg-surface-muted p-3 text-xs leading-relaxed whitespace-pre-line text-ink-muted">
          {agreement.fileCopyNotice}
        </p>
      ) : null}
    </article>
  );
}

function Entry({
  term,
  value,
  className,
}: {
  term: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-ink-muted">{term}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}
